<template>
  <BaseCard>
    <Passcodes
      v-if="showPasscodes"
      @close="showPasscodes = false"
    />
    <Rename
      v-if="showRename && terminal"
      :terminal="terminal"
      @close="showRename = false"
      @renamed="renamedTo = $event"
    />
    <div :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
      <div :class="$style.terminal">
        <img
          v-if="terminalData.model"
          :src="`/img/salonized-pay/onboarding/${terminalData.model.toUpperCase()}.png`"
        >
        <div :class="$style.container">
          <Flex
            :gap="0.5"
            align="center"
          >
            <BaseText
              v-if="terminalData.name || terminalData.model"
              bold
              v-test="'sp-dashboard-terminal-name'"
            >
              {{ terminalData.name || terminalData.model }}
            </BaseText>
            <BaseLabel
              v-if="terminalData.status === 'CONNECTED'"
              color="success"
              v-test="`sp-dashboard-terminals-status-${terminalData.status}`"
            >
              {{ $t('global.enabled') }}
            </BaseLabel>
            <BaseLabel
              v-else-if="terminalData.status"
              v-test="`sp-dashboard-terminals-status-${terminalData.status}`"
            >
              {{ $t('global.inactive') }}
            </BaseLabel>
          </Flex>
          <div
            v-if="multiLocation && terminalData.location"
            v-test="'terminal-location'"
          >
            {{ terminalData.location.internalName }}
          </div>
        </div>
        <div
          v-if="terminalData.status"
          :class="$style.options"
        >
          <BaseMore
            :options="terminalOptions"
            @select="onOptionSelect"
          />
        </div>
      </div>
      <div
        v-if="hasActiveSteps"
        :class="$style.trackOrder"
        v-test="'terminal-tracking'"
      >
        <BaseHeading size="xs">
          {{ $t('register.salonized_pay.tracking.heading') }}
        </BaseHeading>
        <div :class="$style.trackOrderSteps">
          <div
            v-for="(step, index) in steps"
            :key="index"
            :class="[$style.trackOrderStep, { [$style.active]: step.active }]"
            v-test="'terminal-tracking-step'"
          >
            <div :class="$style.trackOrderStepTop">
              <div :class="$style.trackOrderStepIcon">
                <BaseIcon
                  :name="step.icon"
                  size="s"
                  :color="step.active ? 'inverted' : 'secondary'"
                />
              </div>
            </div>
            <div>
              <BaseText
                :bold="step.active"
                :color="step.active ? 'default' : 'secondary'"
                size="s"
              >
                {{ step.heading }}
              </BaseText>
              <div
                v-if="step.active && step.status === 'SHIPPED'"
                v-test="'terminal-tracking-step-description'"
              >
                <BaseText
                  v-if="terminalData.terminalOrder?.courier"
                  size="s"
                  color="secondary"
                >
                  {{ `${$t('register.salonized_pay.tracking.shipped.courier')}: ${terminalData.terminalOrder?.courier}` }}
                </BaseText>
                <BaseText
                  v-if="terminalData.terminalOrder?.trackingCode"
                  size="s"
                  color="secondary"
                >
                  {{ `${$t('register.salonized_pay.tracking.shipped.tracking_id')}: ${terminalData.terminalOrder?.trackingCode}` }}
                </BaseText>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseCard>
</template>

<script
  setup
  lang="ts"
>
import { useI18n } from 'vue-i18n';
import type { IconName } from '@/components/base-icon/types';
import { useCompanyStore } from '@/stores/company';
import type { AdyenTerminalOrder, PosTerminal } from '@/types';
import Passcodes from './Passcodes.vue';
import Rename from './Rename.vue';

const props = defineProps<{
  terminal?: PosTerminal;
  terminalOrder?: AdyenTerminalOrder;
}>();

const renamedTo = ref<string | null>(null);

const terminalData = computed(() => ({
  location: props.terminal?.location || props.terminalOrder?.location,
  model: props.terminal?.model || props.terminalOrder?.terminalModel,
  name: renamedTo.value || props.terminal?.name || '',
  status: props.terminal?.status || null,
  terminalOrder: props.terminal?.terminalOrder || props.terminalOrder,
}));

const showPasscodes = ref(false);
const showRename = ref(false);
const { multiLocation } = useCompanyStore();

const { t } = useI18n();

const terminalOptions = computed(() => [
  {
    name: 'rename',
    icon: 'edit' as IconName,
    label: t('register.salonized_pay.dashboard.terminal.rename'),
  },
  {
    name: 'show_passcode',
    icon: 'lock' as IconName,
    label: t('register.salonized_pay.dashboard.terminal.show_passcode'),
  },
]);

const onOptionSelect = (option: string) => {
  if (option === 'rename') {
    showRename.value = true;
  } else if (option === 'show_passcode') {
    showPasscodes.value = true;
  }
};

const steps = computed(() => [
  {
    status: 'PLACED',
    heading: t('register.salonized_pay.tracking.processing.heading'),
    icon: 'shopping-cart' as IconName,
    active:
      terminalData.value.terminalOrder?.status === 'PLACED' ||
      terminalData.value.terminalOrder?.status === 'SHIPPED' ||
      terminalData.value.terminalOrder?.status === 'DELIVERED',
  },
  {
    status: 'SHIPPED',
    heading: t('register.salonized_pay.tracking.shipped.heading'),
    icon: 'car' as IconName,
    active:
      terminalData.value.terminalOrder?.status === 'SHIPPED' ||
      terminalData.value.terminalOrder?.status === 'DELIVERED',
  },
  {
    status: 'DELIVERED',
    heading: t('register.salonized_pay.tracking.delivered.heading'),
    icon: 'cube' as IconName,
    active: terminalData.value.terminalOrder?.status === 'DELIVERED',
  },
]);

const hasActiveSteps = computed(
  () =>
    terminalData.value.terminalOrder?.showDeliveryProgress &&
    steps.value.some((step) => step.active),
);
</script>

<style
  lang="scss"
  module
>

.terminal {
  display: flex;
  gap: $spacing;

  .base.smallScreen & {
    flex-direction: column;
    align-items: center;
  }
}

.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 50%;
  gap: $spacing * 0.25;

  .base.smallScreen & {
    align-items: center;
  }
}

.icon {
  margin-right: $spacing * 0.2;
}

.options {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;

  .smallScreen & {
    justify-content: center;
  }
}

.trackOrder {
  display: flex;
  flex-direction: column;
  gap: $spacing;
  border-top: 1px solid var(--color-border);
  padding-top: $spacing;
  margin-top: $spacing;
}

.trackOrderSteps {
  display: flex;

  .base:not(.smallScreen) & {
    gap: $spacing * .25;
  }

  .base.smallScreen & {
    flex-direction: column;
    gap: $spacing;
  }
}

.trackOrderStep {
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.5;
  flex: 1;
}

.trackOrderStepTop {
  padding-bottom: $spacing * 0.5;
  border-bottom: 2px solid var(--color-border);

  .trackOrderStep.active & {
    border-color: var(--color-success);
  }
}

.trackOrderStepIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;

  .trackOrderStep.active & {
    background-color: var(--color-success);
  }

  .trackOrderStep:not(.active) & {
    background-color: var(--color-border);
  }
}
</style>
